import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Phone, 
  Mail, 
  Facebook, 
  Instagram, 
  Linkedin,
  Twitter,
  Shield,
  Truck,
  RefreshCw,
  Send
} from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">&lt;/&gt;</span>
              </div>
              <span className="text-xl font-bold">
                <span className="text-white">Re</span>
                <span className="text-orange-500">Busi</span>
                <span className="text-white">.</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm mb-6">
              India's premier marketplace for buying & selling businesses. Trusted by thousands of entrepreneurs.
            </p>
            <div className="space-y-3">
              <a href="tel:+919999999999" className="flex items-center gap-3 text-slate-300 hover:text-orange-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="text-sm">+91 88055 11121</span>
              </a>
              <a href="mailto:support@rebusi.io" className="flex items-center gap-3 text-slate-300 hover:text-orange-400 transition-colors">
                <Mail className="w-4 h-4" />
                <span className="text-sm">support@rebusi.io</span>
              </a>
            </div>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/press" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Press
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Categories</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/marketplace?industry=Manufacturing" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Manufacturing
                </Link>
              </li>
              <li>
                <Link to="/marketplace?industry=Retail" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Retail & E-commerce
                </Link>
              </li>
              <li>
                <Link to="/marketplace?industry=Technology" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Technology
                </Link>
              </li>
              <li>
                <Link to="/franchises" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Franchises
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Support</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-slate-400 hover:text-orange-400 transition-colors text-sm">
                  FAQs
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Stay Updated</h3>
            <p className="text-slate-400 text-sm mb-4">Subscribe to get latest deals & offers.</p>
            <div className="flex gap-2">
              <Input 
                type="email" 
                placeholder="Enter email" 
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                data-testid="footer-email-input"
              />
              <Button className="bg-orange-500 hover:bg-orange-600 px-4" data-testid="footer-subscribe-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {/* Social Icons */}
            <div className="flex gap-4 mt-6">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" 
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:bg-orange-500 hover:text-white transition-all">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:bg-orange-500 hover:text-white transition-all">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:bg-orange-500 hover:text-white transition-all">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:bg-orange-500 hover:text-white transition-all">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-slate-400">
              <Shield className="w-5 h-5 text-orange-500" />
              <span className="text-sm">Secure Transactions</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Truck className="w-5 h-5 text-orange-500" />
              <span className="text-sm">Verified Businesses</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              <span className="text-sm">Expert Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-slate-500 text-sm">
            © 2026 ReBusi. All rights reserved. | ReDefine Business, ReClaim India
          </p>
        </div>
      </div>
    </footer>
  );
}
